import React, { useEffect, useMemo, useRef, useState } from "react";
import { View } from "react-native";
import { useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useAtomValue, useSetAtom } from "jotai/react";
import { ChevronRightIcon } from "lucide-react-native";
import { useForm } from "react-hook-form";
import { toast } from "sonner-native";
import { z } from "zod";

import {
  taxClaimedAsDependentStatusEnumSchema,
  taxDependentsClaimedStatusEnumSchema,
  taxFilingStatusEnumSchema,
  TaxFilingStatusEnumType,
} from "@libs/api/patients";
import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnCheckboxInput } from "@/components/VnCheckboxInput/VnCheckboxInput";
import {
  VnDialog,
  VnDialogContent,
  VnDialogFooter,
  VnDialogHeader,
  VnDialogPortal,
} from "@/components/VnDialog";
import { VnForm } from "@/components/VnForm";
import { VnRadioGroupInput } from "@/components/VnRadioGroupInput/VnRadioGroupInput";
import { VnScrollView } from "@/components/VnScrollView";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateHouseholdMemberRelationshipMutation } from "@/data/mutations/householdMemberRelationshipsMutations";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListHouseholdMemberRelationshipsBetweenMembersQuery } from "@/data/queries/householdMemberRelationshipsQueries";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import {
  householdMemberTaxClaimedAsDependentStatus,
  householdMemberTaxDependentsClaimedStatus,
  householdMemberTaxFilingStatus,
  HouseholdMemberType,
} from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";
import { i18n } from "@/i18n";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";
import { formatHouseholdMemberName } from "@/utils/householdMember";
import {
  buildTaxRelationshipUpdateOperations,
  filterDependentOfRelationships,
  filterDependentRelationships,
} from "@/utils/householdMemberRelationship";
import { useScrollIntoMiddleOnValueChange } from "@/utils/scrollIntoView";

type MemberTaxInformationFormType = Pick<
  HouseholdMemberType,
  | "taxFilingStatus"
  | "taxDependentsClaimedStatus"
  | "taxClaimedAsDependentStatus"
> & {
  dependentOfIds: string[];
  dependentIds: string[];
};

const memberTaxInformationFormSchema = z.object({
  taxDependentsClaimedStatus: taxDependentsClaimedStatusEnumSchema
    .nullable()
    .refine(
      (val): val is z.infer<typeof taxDependentsClaimedStatusEnumSchema> =>
        val !== null,
      { message: i18n.t("common:errors.selectValidOption") },
    ),
  taxClaimedAsDependentStatus: taxClaimedAsDependentStatusEnumSchema
    .nullable()
    .refine(
      (val): val is z.infer<typeof taxClaimedAsDependentStatusEnumSchema> =>
        val !== null,
      { message: i18n.t("common:errors.selectValidOption") },
    ),
  taxFilingStatus: taxFilingStatusEnumSchema
    .nullable()
    .refine(
      (val): val is z.infer<typeof taxFilingStatusEnumSchema> => val !== null,
      { message: i18n.t("common:errors.selectValidOption") },
    ),
  dependentOfIds: z.array(z.string()),
  dependentIds: z.array(z.string()),
});

type MemberTaxInformationFormPropsType = {
  member: HouseholdMemberType;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
  otherMemberIds: string[];
};

const MemberTaxInformationForm: React.FC<MemberTaxInformationFormPropsType> = ({
  member,
  isExpanded,
  onToggleExpand,
  onClose,
  otherMemberIds,
}) => {
  const { t } = useTranslator();

  const {
    mutateAsync: updateHouseholdMember,
    isPending: isUpdateMemberPending,
  } = useUpdateHouseholdMemberMutation();

  const {
    mutateAsync: updateHouseholdMemberRelationship,
    isPending: isUpdateRelationshipPending,
  } = useUpdateHouseholdMemberRelationshipMutation();

  const isPending = isUpdateMemberPending || isUpdateRelationshipPending;

  const { data: relationships } =
    useListHouseholdMemberRelationshipsBetweenMembersQuery({
      primaryHouseholdMemberId: member.id,
      relationshipHouseholdMemberIds: otherMemberIds,
    });

  const existingDependentRelationships = useMemo(
    () => filterDependentRelationships(relationships),
    [relationships],
  );

  const existingDependentOfRelationships = useMemo(
    () => filterDependentOfRelationships(relationships),
    [relationships],
  );

  const defaultFormValues = useMemo(
    (): MemberTaxInformationFormType => ({
      taxFilingStatus: member.taxFilingStatus ?? null,
      taxDependentsClaimedStatus: member.taxDependentsClaimedStatus ?? null,
      taxClaimedAsDependentStatus: member.taxClaimedAsDependentStatus ?? null,
      dependentOfIds: existingDependentOfRelationships.map(rel => rel.id),
      dependentIds: existingDependentRelationships.map(rel => rel.id),
    }),
    [member, existingDependentRelationships, existingDependentOfRelationships],
  );

  const form = useForm<MemberTaxInformationFormType>({
    defaultValues: defaultFormValues,
    resolver: standardSchemaResolver(memberTaxInformationFormSchema),
  });

  const [
    taxFilingStatus,
    taxDependentsClaimedStatus,
    taxClaimedAsDependentStatus,
    dependentIds,
    dependentOfIds,
  ] = form.watch([
    "taxFilingStatus",
    "taxDependentsClaimedStatus",
    "taxClaimedAsDependentStatus",
    "dependentIds",
    "dependentOfIds",
  ]);

  const dependentOptions = useMemo(
    () => relationships?.filter(rel => !dependentOfIds.includes(rel.id)),
    [relationships, dependentOfIds],
  );
  const dependentOfOptions = useMemo(
    () => relationships?.filter(rel => !dependentIds.includes(rel.id)),
    [relationships, dependentIds],
  );

  const shouldShowDependentOptions =
    taxDependentsClaimedStatus ===
      taxDependentsClaimedStatusEnumSchema.enum.CLAIMED &&
    dependentOptions &&
    dependentOptions.length > 0;

  const shouldShowDependentOfOptions =
    taxClaimedAsDependentStatus ===
      taxClaimedAsDependentStatusEnumSchema.enum.CLAIMED_AS_DEPENDENT &&
    dependentOfOptions &&
    dependentOfOptions.length > 0;

  // Refs for scrolling
  const taxFilingStatusCardRef = useRef<View | null>(null);
  const taxDependentsClaimedCardRef = useRef<View | null>(null);
  const taxClaimedAsDependentCardRef = useRef<View | null>(null);
  useScrollIntoMiddleOnValueChange({
    value: taxFilingStatus,
    targetRef: taxFilingStatusCardRef,
  });
  useScrollIntoMiddleOnValueChange({
    value: taxDependentsClaimedStatus,
    targetRef: taxDependentsClaimedCardRef,
  });
  useScrollIntoMiddleOnValueChange({
    value: taxClaimedAsDependentStatus,
    targetRef: taxClaimedAsDependentCardRef,
  });

  const handleSave = async (formData: MemberTaxInformationFormType) => {
    try {
      await updateHouseholdMember({
        id: member.id,
        householdMemberData: {
          taxFilingStatus: formData.taxFilingStatus,
          taxDependentsClaimedStatus: formData.taxDependentsClaimedStatus,
          taxClaimedAsDependentStatus: formData.taxClaimedAsDependentStatus,
        },
      });

      const dependentIds =
        formData.taxDependentsClaimedStatus ===
        taxDependentsClaimedStatusEnumSchema.enum.NOT_CLAIMED
          ? []
          : formData.dependentIds;

      const dependentOfIds =
        formData.taxClaimedAsDependentStatus ===
        taxClaimedAsDependentStatusEnumSchema.enum.NOT_CLAIMED_AS_DEPENDENT
          ? []
          : formData.dependentOfIds;

      const updateRelationshipOperations = buildTaxRelationshipUpdateOperations(
        {
          existingDependentIds: existingDependentRelationships.map(
            rel => rel.id,
          ),
          existingDependentOfIds: existingDependentOfRelationships.map(
            rel => rel.id,
          ),
          newDependentIds: dependentIds,
          newDependentOfIds: dependentOfIds,
        },
      );

      if (updateRelationshipOperations.length > 0) {
        const updateRelationshipPromises = updateRelationshipOperations.map(
          operation =>
            updateHouseholdMemberRelationship({
              id: operation.relationshipId,
              relationshipData: {
                taxRelationship: operation.taxRelationship,
              },
            }),
        );

        await Promise.all(updateRelationshipPromises);
      }

      onToggleExpand();
      onClose();
    } catch {
      toast.error(
        t(
          "native:applicationTaxInformationScreen.errors.unableToUpdateRelationship",
        ),
      );
    }
  };

  useEffect(() => {
    if (isExpanded) {
      form.reset(defaultFormValues);
    }
  }, [isExpanded, defaultFormValues, form]);

  const formatTaxFilingStatus = (status: TaxFilingStatusEnumType | null) => {
    if (!status)
      return t("native:applicationTaxInformationScreen.needsTaxInformation");
    return t(`common:taxFilingStatus.${status}`);
  };

  return (
    <VnForm form={form} isInputOnlyForm>
      <VnCard className="gap-1 rounded-md p-4">
        <VnBox className="flex flex-row items-center justify-between">
          <VnText variant="lgMedium">
            {formatHouseholdMemberName(member)}
          </VnText>
          <VnButton
            size="sm"
            variant="ghost"
            IconComponent={ChevronRightIcon}
            iconPosition="right"
            onPress={onToggleExpand}>
            <VnText i18nKey="common:edit" />
          </VnButton>
        </VnBox>
        <VnText variant="lg">
          {formatTaxFilingStatus(member.taxFilingStatus ?? null)}
        </VnText>
        {existingDependentRelationships.length > 0 && (
          <VnText
            variant="lg"
            i18nKey="native:applicationTaxInformationScreen.memberCard.dependents"
            i18nOptions={{
              name: existingDependentRelationships
                .map(rel => formatHouseholdMemberName(rel.relationshipMember))
                .join(", "),
            }}
          />
        )}
        {existingDependentOfRelationships.length > 0 && (
          <VnText
            variant="lg"
            i18nKey="native:applicationTaxInformationScreen.memberCard.dependentOf"
            i18nOptions={{
              name: existingDependentOfRelationships
                .map(rel => formatHouseholdMemberName(rel.relationshipMember))
                .join(", "),
            }}
          />
        )}
        {/* Dialog for editing tax information for the member */}
        <VnDialog open={isExpanded} onOpenChange={onToggleExpand}>
          <VnDialogPortal>
            <VnDialogContent className="bg-background m-0 flex h-[100dvh] w-screen max-w-none flex-col gap-2 rounded-none p-4 sm:m-auto sm:h-[90dvh] sm:max-w-lg sm:rounded-lg">
              <VnDialogHeader className="relative shrink-0 justify-end">
                <VnText
                  variant="headerTwo"
                  className="absolute left-0 right-0 flex items-center justify-center"
                  i18nKey="native:applicationTaxInformationScreen.memberCard.memberDialog.title"
                  i18nOptions={{ name: formatHouseholdMemberName(member) }}
                />
              </VnDialogHeader>
              <VnText
                variant="lg"
                className="text-muted-foreground shrink-0 text-center"
                i18nKey="native:applicationTaxInformationScreen.memberCard.memberDialog.description"
              />
              <VnScrollView showsVerticalScrollIndicator>
                <VnBox className="mt-4 gap-4">
                  {/* Tax Filing Status */}
                  <View ref={taxFilingStatusCardRef}>
                    <VnCard className="p-4">
                      <VnText
                        variant="lgMedium"
                        i18nKey="native:applicationTaxInformationScreen.memberCard.memberDialog.taxFilingStatus"
                      />
                      <VnRadioGroupInput
                        name="taxFilingStatus"
                        control={form.control}
                        options={householdMemberTaxFilingStatus}
                        radioGroupClassName="gap-6 bg-background p-4 rounded-md"
                        radioItemClassName="bg-card"
                      />
                    </VnCard>
                  </View>

                  {/* Tax Dependents Claimed Status */}
                  <View ref={taxDependentsClaimedCardRef}>
                    <VnCard className="p-4">
                      <VnText
                        variant="lgMedium"
                        i18nKey="native:applicationTaxInformationScreen.memberCard.memberDialog.taxDependentsClaimedStatus"
                      />
                      <VnRadioGroupInput
                        name="taxDependentsClaimedStatus"
                        control={form.control}
                        options={householdMemberTaxDependentsClaimedStatus}
                        radioGroupClassName="gap-6"
                      />
                      {shouldShowDependentOptions && (
                        <VnBox className="bg-background gap-4 rounded-md p-4">
                          <VnText
                            variant="lgMedium"
                            i18nKey="native:applicationTaxInformationScreen.memberCard.memberDialog.who"
                          />
                          <VnCheckboxInput
                            name="dependentIds"
                            control={form.control}
                            hasMultipleOptions
                            inputClassName="bg-card"
                            options={dependentOptions.map(rel => ({
                              value: rel.id,
                              label: formatHouseholdMemberName(
                                rel.relationshipMember,
                              ),
                            }))}
                          />
                        </VnBox>
                      )}
                    </VnCard>
                  </View>
                  {/* Claimed As Dependent Status */}
                  <View ref={taxClaimedAsDependentCardRef}>
                    <VnCard className="p-4">
                      <VnText
                        variant="lgMedium"
                        i18nKey="native:applicationTaxInformationScreen.memberCard.memberDialog.taxClaimedAsDependentStatus"
                        i18nOptions={{
                          name: formatHouseholdMemberName(member),
                        }}
                      />
                      <VnRadioGroupInput
                        name="taxClaimedAsDependentStatus"
                        control={form.control}
                        options={householdMemberTaxClaimedAsDependentStatus}
                        radioGroupClassName="gap-6"
                      />
                      {shouldShowDependentOfOptions && (
                        <VnBox className="bg-background gap-4 rounded-md p-4">
                          <VnText
                            variant="lgMedium"
                            i18nKey="native:applicationTaxInformationScreen.memberCard.memberDialog.who"
                          />
                          <VnCheckboxInput
                            name="dependentOfIds"
                            control={form.control}
                            hasMultipleOptions
                            inputClassName="bg-card"
                            isRequired={false}
                            options={dependentOfOptions.map(rel => ({
                              value: rel.id,
                              label: formatHouseholdMemberName(
                                rel.relationshipMember,
                              ),
                            }))}
                          />
                        </VnBox>
                      )}
                    </VnCard>
                  </View>
                </VnBox>
              </VnScrollView>
              <VnDialogFooter className="mt-4 shrink-0">
                <VnButton
                  size="lg"
                  variant="outline"
                  i18nKey="common:cancel"
                  onPress={() => {
                    form.reset(defaultFormValues);
                    onClose();
                  }}
                />
                <VnButton
                  size="lg"
                  i18nKey="native:applicationTaxInformationScreen.memberCard.memberDialog.saveAndReturn"
                  onPress={() => {
                    void form.handleSubmit(handleSave)();
                  }}
                  isLoading={isPending}
                />
              </VnDialogFooter>
            </VnDialogContent>
          </VnDialogPortal>
        </VnDialog>
      </VnCard>
    </VnForm>
  );
};

export const ApplicationTaxInformationScreen = () => {
  const router = useRouter();
  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);
  const { mutate: updateCurrentPatient } = useUpdateCurrentPatientMutation();

  const { data: householdMembers } = useListHouseholdMembersQuery();

  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  const handleToggleExpand = (memberId: string) => {
    setExpandedMemberId(prev => (prev === memberId ? null : memberId));
  };

  const handleClose = () => {
    setExpandedMemberId(null);
  };

  const isNextDisabled = useMemo(() => {
    if (!householdMembers) return true;
    return householdMembers.some(
      member =>
        !member.taxFilingStatus ||
        !member.taxDependentsClaimedStatus ||
        !member.taxClaimedAsDependentStatus,
    );
  }, [householdMembers]);

  const handleNext = () => {
    if (isNextDisabled) {
      return;
    }

    updateCurrentPatient(
      {
        patientData: {
          applicationLastStep: "TAX_INFORMATION",
        },
      },
      {
        onSuccess: () => {
          setApplicationCurrentStep(step =>
            Math.min(step + 1, applicationTotalSteps),
          );

          router.navigate({
            pathname: "/(authed)/(modal)/application/work-and-income",
          });
        },
      },
    );
  };

  return (
    <ApplicationScreenLayout
      onNextButtonPress={handleNext}
      isNextButtonDisabled={isNextDisabled}
      titleI18nKey="native:applicationTaxInformationScreen.title"
      descriptionI18nKey="native:applicationTaxInformationScreen.description">
      <VnBox className="gap-4">
        {householdMembers?.map(member => {
          const isExpanded = expandedMemberId === member.id;
          const otherMemberIds =
            householdMembers?.filter(m => m.id !== member.id).map(m => m.id) ??
            [];
          return (
            <MemberTaxInformationForm
              key={member.id}
              member={member}
              otherMemberIds={otherMemberIds}
              isExpanded={isExpanded}
              onToggleExpand={() => handleToggleExpand(member.id)}
              onClose={handleClose}
            />
          );
        })}
      </VnBox>
    </ApplicationScreenLayout>
  );
};
